/* eslint-disable rover/no-platform-specific-globals-or-imports */
import uuid from 'uuid';

import { UserInformation } from '@rover/rsdk/src/apiClient/latest';
import {
  CookiePurpose,
  createCookie,
  getCookie,
  hasCookie,
} from '@rover/rsdk/src/modules/Network/cookies';
import { snakeify } from '@rover/rsdk/src/modules/Network/object-utilities';
import { isBot as isUaBot, isMobileEmbedded } from '@rover/rsdk/src/modules/Network/userAgent';

export const getAloomaSessionKey = (): string | null | undefined => {
  if (hasCookie('alooma_session_key')) {
    return getCookie('alooma_session_key');
  }

  const sessionId = uuid.v4();
  createCookie(CookiePurpose.Performance, 'alooma_session_key', sessionId, 365);
  return sessionId;
};

export const getGoogleAnalyticsClientId = (): string | null => {
  const clientIdCookie = getCookie('_ga');
  return clientIdCookie ? clientIdCookie.substring(6) : null;
};

const configureAlooma = ({
  currentUser,
  pageCategory = 'uncategorized',
  language,
  countryCode,
  enableAmplitudeAnalytics = false,
}: {
  currentUser?: UserInformation;
  pageCategory?: string;
  language?: string;
  countryCode: string;
  enableAmplitudeAnalytics: boolean;
}): void => {
  const timezoneOffset = new Date().getTimezoneOffset();
  const environment = process.env.NODE_ENV;
  const commonIdentifier = getCookie('rover_cid');
  const { userAgent } = navigator;
  const isBot = isUaBot(userAgent);
  const sessionId = getAloomaSessionKey();
  const aloomaConfig = snakeify({
    timezoneOffset,
    environment,
    commonIdentifier,
    userAgent,
    isBot,
    ...(currentUser?.opk && {
      personOpk: currentUser.opk,
      eh: currentUser.emailHash,
    }),
    ...(!currentUser?.opk && {
      eh: null,
    }),
    ...(sessionId && {
      sessionId,
    }),
    language,
    countryCode,
  });

  window.Rover = window.Rover || {};
  window.Rover.pageCategory = pageCategory;
  window.Rover.eventstreamEnableAmplitude = enableAmplitudeAnalytics;

  if (window.eventstream?.amplitudeConfigure) {
    window.eventstream.amplitudeConfigure(
      currentUser?.opk,
      {
        ...(currentUser?.opk ? { is_new_customer: currentUser.isNewCustomer } : {}),
        ...(currentUser?.opk
          ? { is_active_service_provider: currentUser.isActiveServiceProvider }
          : {}),
        last_google_analytics_client_id: getGoogleAnalyticsClientId(),
      },
      {
        is_mobile_embedded: isMobileEmbedded(),
      }
    );
  }

  if (window.alooma) {
    // Configure Alooma (old client) values
    window.alooma.register(aloomaConfig);
    window.alooma.unregister('funnelVersion'); // Clear this from the cookie so we can set it only for pages that need it
    window.alooma.unregister('isQuickMatchRebook'); // Clear this from the cookie so we can set it only for pages that need it
    window.alooma.unregister('isRoverGoMarket'); // Clear this from the cookie so we can set it only for pages that need it

    // Manually fire mp_page_view event, to work around a render timing issue
    window.alooma.track_pageview();
  }
};

export default configureAlooma;
